import React, { useEffect, useState } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { apiService } from '../../services/api';
import { useAuth } from '../../contexts/ClerkAuthContext';

interface ErrorGroup {
  id: number;
  organization_id: string;
  fingerprint_primary: string;
  fingerprint_secondary: string;
  error_type: string;
  representative_message: string;
  representative_stack: string[];
  first_seen_at: string;
  last_seen_at: string;
  occurrence_count: number;
  status: 'active' | 'resolved' | 'ignored';
  resolved_at?: string;
  created_at: string;
}

interface ErrorInstance {
  id: number;
  organization_id: string;
  group_id: number;
  source: 'client' | 'server';
  type: string;
  message: string;
  stack: string[];
  context: Record<string, any>;
  session_data: Record<string, any>;
  occurred_at: string;
  fingerprint_primary: string;
  fingerprint_secondary: string;
  is_resolved: boolean;
  created_at: string;
}

interface ErrorDetailModalProps {
  group: ErrorGroup;
  appId?: string;
  onClose: () => void;
  onRefresh: () => void;
}

export const ErrorDetailModal: React.FC<ErrorDetailModalProps> = ({ group, appId, onClose, onRefresh }) => {
  const { getToken } = useAuth();
  const [instances, setInstances] = useState<ErrorInstance[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedInstance, setSelectedInstance] = useState<ErrorInstance | null>(null);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchErrorInstances();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group.id]);

  const fetchErrorInstances = async () => {
    try {
      setLoading(true);
      const token = await getToken();
      if (!token) return;

      // Use app-scoped API if appId is provided
      const response = appId
        ? await apiService.getErrorsByGroupForApp(token, appId, group.id)
        : await apiService.getErrorsByGroup(token, group.id);

      setInstances(response.errors || []);
    } catch (err) {
      console.error('Failed to fetch error instances:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (status: 'active' | 'resolved' | 'ignored') => {
    try {
      setUpdating(true);
      const token = await getToken();
      if (!token) return;

      // Use app-scoped API if appId is provided
      if (appId) {
        await apiService.updateErrorGroupStatusForApp(token, appId, group.id, status);
      } else {
        await apiService.updateErrorGroupStatus(token, group.id, status);
      }

      onRefresh();
      onClose();
    } catch (err) {
      console.error('Failed to update error status:', err);
    } finally {
      setUpdating(false);
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Badge className={group.status === 'active' ? 'bg-red-100 text-red-800' : group.status === 'resolved' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                  {group.status}
                </Badge>
                <span className="text-sm font-mono text-gray-600">{group.error_type}</span>
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-1">
                {group.representative_message}
              </h2>
              <p className="text-sm text-gray-600">
                {group.occurrence_count} {group.occurrence_count === 1 ? 'occurrence' : 'occurrences'}
                {' • '}
                First seen {formatTimestamp(group.first_seen_at)}
                {' • '}
                Last seen {formatTimestamp(group.last_seen_at)}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 ml-4"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Actions */}
          <div className="mt-4 flex gap-2">
            <Button
              size="sm"
              variant={group.status === 'resolved' ? 'default' : 'outline'}
              onClick={() => handleStatusChange('resolved')}
              disabled={updating || group.status === 'resolved'}
            >
              {group.status === 'resolved' ? 'Resolved' : 'Mark as Resolved'}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleStatusChange('ignored')}
              disabled={updating || group.status === 'ignored'}
            >
              {group.status === 'ignored' ? 'Ignored' : 'Ignore'}
            </Button>
            {group.status !== 'active' && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleStatusChange('active')}
                disabled={updating}
              >
                Reopen
              </Button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {/* Stack Trace */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Stack Trace</h3>
            <Card className="p-4 bg-gray-50">
              <pre className="text-xs font-mono text-gray-800 whitespace-pre-wrap">
                {group.representative_stack.join('\n')}
              </pre>
            </Card>
          </div>

          {/* Recent Instances */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Recent Occurrences</h3>
            {loading ? (
              <div className="animate-pulse space-y-2">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-16 bg-gray-200 rounded"></div>
                ))}
              </div>
            ) : instances.length === 0 ? (
              <Card className="p-4 text-center text-gray-500">
                No error instances found
              </Card>
            ) : (
              <div className="space-y-2">
                {instances.slice(0, 10).map(instance => (
                  <Card
                    key={instance.id}
                    className="p-3 cursor-pointer hover:bg-gray-50"
                    onClick={() => setSelectedInstance(selectedInstance?.id === instance.id ? null : instance)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          {instance.source}
                        </Badge>
                        <span className="text-sm text-gray-600">
                          {formatTimestamp(instance.occurred_at)}
                        </span>
                      </div>
                      <svg
                        className={`w-4 h-4 text-gray-400 transition-transform ${selectedInstance?.id === instance.id ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>

                    {selectedInstance?.id === instance.id && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        {/* Context */}
                        {instance.context && Object.keys(instance.context).length > 0 && (
                          <div className="mb-3">
                            <h4 className="text-sm font-semibold text-gray-700 mb-1">Context</h4>
                            <pre className="text-xs bg-gray-100 p-2 rounded overflow-auto max-h-40">
                              {JSON.stringify(instance.context, null, 2)}
                            </pre>
                          </div>
                        )}

                        {/* Session Data */}
                        {instance.session_data && Object.keys(instance.session_data).length > 0 && (
                          <div>
                            <h4 className="text-sm font-semibold text-gray-700 mb-1">Session</h4>
                            <pre className="text-xs bg-gray-100 p-2 rounded overflow-auto max-h-40">
                              {JSON.stringify(instance.session_data, null, 2)}
                            </pre>
                          </div>
                        )}

                        {/* Stack Trace for this instance */}
                        {instance.stack && instance.stack.length > 0 && (
                          <div className="mt-3">
                            <h4 className="text-sm font-semibold text-gray-700 mb-1">Stack Trace</h4>
                            <pre className="text-xs bg-gray-100 p-2 rounded overflow-auto max-h-40 font-mono">
                              {instance.stack.join('\n')}
                            </pre>
                          </div>
                        )}
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
